/**
 * useStudentShopState — les gestes de la boutique, et ses deux écritures.
 *
 * `MyPetalsTabScreen.js` et les écrans de félicitations qu'il poussait :
 *  - la croix : retour, ou le menu quand la pile est vide ;
 *  - un geste ouvre sa fiche ; « Acheter » y envoie `shopService.purchase { id }` ;
 *  - un achat qui passe ouvre l'accusé dans la même feuille. La quête de Tina y est lue dans la
 *    réponse (`skill_sets`, la quête 4), et son évolution est marquée lue
 *    (`markSkillSetEvolutionAsRead`, ce que faisait `MyProgressCongrats`) ;
 *  - un achat refusé referme la fiche, se dit dans l'avis de la page, et le compte est relu (le
 *    rappel d'erreur de l'ancien écran) ;
 *  - un achat qui donne un nouveau titre joue, à l'ouverture de l'accusé, le son de l'ancien écran du
 *    titre (`new-title.mp3`, `NewTitleCongrats`) ; « Choisir ce titre à la place de… » :
 *    `updateTitleSkillSet { skill_set_id }` ;
 *  - « Partager mes statistiques », sur l'application seulement : l'écran `share-stats` de l'ancien
 *    code, qui ne lit du geste que son identifiant et son nom ;
 *  - la quête de l'accusé se touche : « Mes quêtes » (`skill-set`, `MyProgressCongrats`), la feuille
 *    refermée pour ne pas rester par-dessus ; sa barre qui se remplit joue le son de l'ancienne
 *    (`progress.mp3`, `ProgressBarM`) ;
 *  - l'accusé ouvert émet les événements des écrans de félicitations qu'il remplace
 *    (`purchaseCongratsEvents`, les noms de l'ancien code).
 *
 * L'achat passe par `useApiMutation`, mais son succès lit aussi `all_data` de la tranche (la réponse
 * entière : le compte sous `data`, les quêtes à côté) : un ref suit la tranche à chaque rendu, lu
 * dans `onSuccess`, qui part dans l'effet de ce même rendu.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useNavigation } from "@react-navigation/native"
import { useMemo, useReducer, useRef } from "react"
import { Platform } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { shopService } from "../../../../../../data/services/shopService"
import { skillSetsService } from "../../../../../../data/services/skillSetsService"
import { playUiSound } from "../../../../../../engine/precall/playSound"
import { createTelemetry } from "../../../../../../engine/shared/telemetry"
import {
    announcesNewTitle,
    GESTURES_SKILL_SET_ID,
    gesturesSkillSetOf,
    type ApiPurchaseResponse,
} from "../../../../adapters/student/Game/shopAdapter"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useBackOr } from "../../../hooks/useBackOr"
import { type UIShopGesture } from "../../../types/studentGame"
import { purchaseCongratsEvents } from "./shopCongratsEvents"
import { initialStudentShopUiState, studentShopUiReducer } from "./studentShopUiState"

type StackNavigation = { push: (route: string, params?: object) => void }

const PURCHASE_FAILED = "Ton achat n'a pas pu être enregistré. Réessaie dans un instant."
const TITLE_FAILED = "Ton titre n'a pas pu être changé. Réessaie dans un instant."

/* eslint-disable @typescript-eslint/no-require-imports */
/** Le son de la barre de quête qui avance (`ProgressBarM`). */
const PROGRESS_SOUND = require("../../../../../../../assets/sounds/progress.mp3") as number
/** Le son de l'ancien écran du nouveau titre (`NewTitleCongrats`), à l'ouverture de l'accusé qui l'annonce. */
const NEW_TITLE_SOUND = require("../../../../../../../assets/sounds/new-title.mp3") as number
/* eslint-enable @typescript-eslint/no-require-imports */

/** Un son qui ne part pas pour une autre raison qu'un refus attendu du navigateur se dit dans Bugsnag. */
const shopTelemetry = createTelemetry("game-student.shop", { role: "student" })

export function useStudentShopState() {
    const navigation = useNavigation() as unknown as StackNavigation
    const dispatch = useDispatch()
    const [ui, dispatchUi] = useReducer(studentShopUiReducer, initialStudentShopUiState)

    const purchaseResponse = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.purchase?.all_data as ApiPurchaseResponse
    )
    const purchaseResponseRef = useRef<ApiPurchaseResponse>(purchaseResponse)
    purchaseResponseRef.current = purchaseResponse

    const { start: startPurchase } = useApiMutation({
        service: shopService.purchase,
        voice: "student",
        fallback: PURCHASE_FAILED,
        skipWhilePending: true,
        onSuccess: () => {
            const gesturesSkillSet = gesturesSkillSetOf(purchaseResponseRef.current)
            dispatchUi({ type: "PURCHASED", gesturesSkillSet })
            for (const event of purchaseCongratsEvents(gesturesSkillSet)) amplitude.track(event)
            if (announcesNewTitle(gesturesSkillSet)) {
                playUiSound(NEW_TITLE_SOUND, (cause) => shopTelemetry.error("new-title-sound", cause))
            }
            if (gesturesSkillSet?.id != null) {
                dispatch(
                    apiRequest(
                        skillSetsService.markSkillSetEvolutionAsRead,
                        {},
                        { skill_set_ids: [gesturesSkillSet.id] }
                    ) as never
                )
            }
        },
        onError: (message) => {
            dispatchUi({ type: "PURCHASE_FAILED", message })
            dispatch(apiRequest(authService.getUser) as never)
        },
    })

    const { start: startTitle } = useApiMutation({
        service: authService.updateTitleSkillSet,
        voice: "student",
        fallback: TITLE_FAILED,
        onError: (message) => dispatchUi({ type: "FAILED", message }),
    })

    const gesture = ui.gesture
    const isApp = Platform.OS !== "web"

    /** Ce que la feuille reçoit de l'état ; le geste, l'accusé et les attentes viennent du view-model. */
    const sheet = useMemo(
        () => ({
            step: ui.step,
            onBuy: () => {
                if (gesture != null) startPurchase({ id: gesture.id })
            },
            onClose: () => dispatchUi({ type: "CLOSE" }),
            onShare:
                isApp && gesture != null
                    ? () => navigation.push("share-stats", { shopProduct: { id: gesture.id, name: gesture.name } })
                    : undefined,
            onChooseNewTitle: () => {
                startTitle({}, { skill_set_id: GESTURES_SKILL_SET_ID })
            },
            onOpenQuest: () => {
                dispatchUi({ type: "CLOSE" })
                navigation.push("skill-set", { skillSetId: GESTURES_SKILL_SET_ID })
            },
            onQuestFilled: () =>
                playUiSound(PROGRESS_SOUND, (cause) => {
                    shopTelemetry.error("quest-progress-sound", cause)
                }),
        }),
        [ui.step, gesture, isApp, startPurchase, startTitle, navigation]
    )

    const onClose = useBackOr("menu")

    return {
        ui,
        sheet,
        onClose,
        onOpenGesture: (opened: UIShopGesture) =>
            dispatchUi({ type: "OPEN", gesture: { id: opened.id, name: opened.name } }),
        notice: ui.notice != null ? { message: ui.notice, tone: "danger" as const } : null,
        onDismissNotice: () => dispatchUi({ type: "DISMISS_NOTICE" }),
    }
}
